import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Form } from 'src/models/form';
import { useFormProvider } from 'src/context/FormProviderContext';
import FormComponent from 'src/components/FormComponent/FormComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from 'src/components/_common/Spinner/Spinner';
import { HiExternalLink, HiPrinter } from 'react-icons/hi';
import PageHeader from 'src/components/_common/PageHeader/PageHeader';
import Button from 'src/components/_common/Button/Button';
import ClassesContext from 'src/hooks/useClasses';
import ShowGuidancePdf from '../FormListPage/ShowGuidancePdf';
import UsersContext from 'src/hooks/useUsers';

export const FormPage = ({ canDeliver = true }: { canDeliver?: boolean }) => {
  const formProvider = useFormProvider();
  const [form, setForm] = useState<Form | undefined>(undefined);
  const [formLoaded, setFormLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { classId, userId, studentUserId, formId, assignmentId } = useParams<{
    classId: string;
    userId: string;
    studentUserId: string;
    formId: string;
    assignmentId: string;
  }>();
  const intl = useIntl();
  const { currentClass, getCurrentClass } = ClassesContext.useContainer();
  const [title, setTitle] = useState('');
  const { user, getUser } = UsersContext.useContainer();

  useEffect(() => {
    if (formId) {
      formProvider.getForm(Number.parseInt(formId, 10)).then(receivedForm => {
        const sortForm = (obj: Form) => {
          const temp = {
            ...obj,
            groups: obj?.groups.sort((a, b) => (a.order > b.order ? 1 : -1)),
          };
          return temp;
        };
        if (receivedForm) setForm(sortForm(receivedForm));
        setFormLoaded(true);
      });
    }
  }, [formId]);

  useEffect(() => {
    window.addEventListener('beforeunload', clearState);
    return () => {
      window.removeEventListener('beforeunload', clearState);
    };
  }, []);

  const clearState = () => {
    window.history.replaceState({}, '');
  };

  useEffect(() => {
    if (classId && currentClass?.id !== +classId) getCurrentClass(+classId);
    if (userId && user?.id !== +userId) getUser(+userId, classId ? +classId : undefined);
  }, [classId]);

  useEffect(() => {
    if (currentClass && form) {
      const isMidSemester = currentClass.assignments?.find(
        assignment =>
          classId && assignmentId &&
          assignment.classId === +classId &&
          assignment.id === +assignmentId,
      )?.isMidSemester;
      setTitle(
        `${
          isMidSemester
            ? intl.formatMessage({ id: 'mid-semester' })
            : intl.formatMessage({ id: 'end-semester' })
        } - ${form.name}`,
      );
    } else if (form) {
      setTitle(form.name);
    }
  }, [currentClass, form, assignmentId, classId]);

  const ifHasPath: string = (location.state as any)?.pPath;
  if (!ifHasPath) {
    navigate('/', { replace: true });
  }

  function openIndependenceScale() {
    window.open('/independence-scale', '_blank');
  }
  const openGuidancePdf = () => {
    if (formId) ShowGuidancePdf(Number.parseInt(formId, 10));
  };

  if (!formLoaded && ifHasPath) return <Spinner />;
  if (!ifHasPath) return <></>;

  return (
    <>
      <PageHeader title={title}>
        <div className="flex justify-center items-center text-darkerGray">
          {user?.displayName}
        </div>
        <section className='mt-2'>
          <Button
            onClick={openIndependenceScale}
            iconRight={HiExternalLink}
            layout="outline"
            className="mr-2"
          >
            <FormattedMessage id="independenceScale" />
          </Button>
          <Button onClick={openGuidancePdf} iconLeft={HiPrinter}>
            <FormattedMessage id="guidance-pdf" />
          </Button>
        </section>
      </PageHeader>
      {form && (
        <FormComponent
          key={`${studentUserId}-${assignmentId}`}
          form={form}
          canDeliver={canDeliver}
        />
      )}
      {!form && (
        <h2 data-testid="form-not-found">
          <FormattedMessage id="form-not-found" />
        </h2>
      )}
    </>
  );
};

export default FormPage;
